import { useCallback, useContext } from "react";

import { Icon, Item, Listbox, Span } from "@icg360/design-system";

import { MSSLink } from "components/common/link";
import {
  AuthAppContext,
  AuthAppDispatchContext,
} from "components/root/auth-app-provider";

import styles from "./mobile-policy-selector.module.scss";

type MobilePolicySelectorProps = {
  onClose: () => void;
};

export const MobilePolicySelector = ({
  onClose,
}: MobilePolicySelectorProps) => {
  const { policies, selectedPolicyId } = useContext(AuthAppContext);
  const { setPolicy } = useContext(AuthAppDispatchContext);

  const onSelectionChange = useCallback(
    (selection: Set<string>) => {
      onClose();
      const selectedPolicyId = selection.keys().next().value;
      const selectedPolicy = policies?.find(
        ({ policyId }) => policyId === selectedPolicyId
      );
      if (selectedPolicy) {
        setPolicy(selectedPolicy);
      }
    },
    [onClose, policies, setPolicy]
  );

  return policies ? (
    <div className={styles.policySelector}>
      <Listbox
        selectedKeys={new Set([selectedPolicyId])}
        selectionMode="single"
        onSelectionChange={onSelectionChange}
        className={styles.selectorBoxDS}
        aria-label="policies"
      >
        {policies.map((policy) => {
          const { propertyAddress, policyId, isCommercial } = policy ?? {};
          const selected = policyId === selectedPolicyId;
          return (
            <Item
              key={policyId}
              textValue={`${propertyAddress?.street1} ${propertyAddress?.street2} policy number ${policyId}`}
            >
              <div className={styles.policy}>
                <Icon name={isCommercial ? "Store" : "Home"} />
                <div className={styles.address}>
                  <Span color={selected ? "inverse" : "default"} bold>
                    {propertyAddress?.street1}
                  </Span>
                  {propertyAddress?.street2 ? (
                    <Span color={selected ? "inverse" : "default"} bold>
                      {propertyAddress?.street2}
                    </Span>
                  ) : null}
                  {`(Policy #${policyId})`}
                </div>
              </div>
            </Item>
          );
        })}
      </Listbox>
      <MSSLink className={styles.addPolicyLink} to="/my/settings/policies">
        <div className={styles.addPolicyIcon}>
          <Icon name="AddCircle" />
        </div>
        <div className={styles.addPolicyLabel}>Add policy</div>
      </MSSLink>
    </div>
  ) : null;
};
